'use client';
import React, { useState, useEffect } from 'react';
import {
  History,
  TrendingUp,
  TrendingDown,
  Filter,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface DbSignal {
  id: string;
  direction: string | null;
  entry_best_low: number | null;
  entry_best_high: number | null;
  tp1: number | null;
  trade_sl: number | null;
  overall_setup_score: number | null;
  direction_confidence: number | null;
  machine_learning_score: number | null;
  current_price: number | null;
  entry_status: string | null;
  mode: string | null;
  created_at: string;
  outcome: string | null;
  pips_result: number | null;
  outcome_checked_at: string | null;
}

interface HistoryRow {
  id: string;
  direction: 'BUY' | 'SELL';
  entryPrice: string;
  aiScore: number;
  entryStatus: string;
  mode: string;
  timestamp: string;
  outcome: string | null;
  pipsResult: number | null;
}

type DirectionFilter = 'ALL' | 'BUY' | 'SELL';

const ITEMS_PER_PAGE = 8;

function mapDbToRow(s: DbSignal): HistoryRow {
  const dir: 'BUY' | 'SELL' = s.direction === 'SELL' ? 'SELL' : 'BUY';
  const entryLow = s.entry_best_low != null ? Number(s.entry_best_low).toFixed(2) : 'N/A';
  const entryHigh = s.entry_best_high != null ? Number(s.entry_best_high).toFixed(2) : 'N/A';
  const entryPrice = entryLow !== 'N/A' && entryHigh !== 'N/A' ? `${entryLow}–${entryHigh}` : 'N/A';
  const aiScore = s.overall_setup_score ?? 0;
  const d = new Date(s.created_at);
  const pad = (n: number) => String(n).padStart(2, '0');
  const timestamp = `${d.getUTCDate()} ${d.toLocaleString('en', { month: 'short', timeZone: 'UTC' })} ${d.getUTCFullYear()} · ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;

  return {
    id: `XAU-${String(s.id).slice(-4).toUpperCase()}`,
    direction: dir,
    entryPrice,
    aiScore,
    entryStatus: s.entry_status || 'N/A',
    mode: s.mode || 'STANDARD',
    timestamp,
    outcome: s.outcome ?? null,
    pipsResult: s.pips_result != null ? Number(s.pips_result) : null,
  };
}

function OutcomeBadge({ outcome }: { outcome: string | null }) {
  if (!outcome) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono font-semibold bg-zinc-800/60 text-zinc-500 border border-white/5">
        <Clock className="w-2.5 h-2.5" />
        PENDING
      </span>
    );
  }
  if (outcome === 'TP1_HIT') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
        <CheckCircle2 className="w-2.5 h-2.5" />
        TP1 HIT
      </span>
    );
  }
  if (outcome === 'SL_HIT') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono font-semibold bg-rose-500/10 text-rose-400 border border-rose-500/20">
        <XCircle className="w-2.5 h-2.5" />
        SL HIT
      </span>
    );
  }
  if (outcome === 'EXPIRED') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono font-semibold bg-zinc-700/40 text-zinc-400 border border-zinc-600/30">
        <Clock className="w-2.5 h-2.5" />
        EXPIRED
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono font-semibold bg-zinc-800/60 text-zinc-500 border border-white/5">
      {outcome}
    </span>
  );
}

function PipsCell({ pips }: { pips: number | null }) {
  if (pips == null) return <span className="text-xs font-mono text-zinc-600">—</span>;
  const isPos = pips >= 0;
  return (
    <span className={`text-xs font-mono font-semibold tabular-nums ${isPos ? 'text-emerald-400' : 'text-rose-400'}`}>
      {isPos ? '+' : ''}{pips.toFixed(1)}
    </span>
  );
}

function HistoryTableRow({ entry, isOdd }: { entry: HistoryRow; isOdd: boolean }) {
  const isBuy = entry.direction === 'BUY';
  return (
    <tr className={`border-b border-white/5 transition-colors duration-100 hover:bg-zinc-800/30 ${isOdd ? 'bg-zinc-900/20' : ''}`}>
      <td className="px-3 sm:px-5 py-3.5 whitespace-nowrap">
        <span className="text-xs font-mono text-zinc-400">{entry.id}</span>
      </td>
      <td className="px-3 sm:px-5 py-3.5 whitespace-nowrap">
        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-mono font-bold ${isBuy ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'}`}>
          {isBuy ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {entry.direction}
        </div>
      </td>
      <td className="px-3 sm:px-5 py-3.5 whitespace-nowrap">
        <span className="text-xs font-mono text-zinc-300 tabular-nums">{entry.entryPrice}</span>
      </td>
      <td className="px-3 sm:px-5 py-3.5 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <div className="w-16 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${entry.aiScore}%`,
                background: entry.aiScore >= 75 ? '#34d399' : entry.aiScore >= 50 ? '#fbbf24' : '#f87171',
              }}
            />
          </div>
          <span className="text-xs font-mono text-zinc-300 tabular-nums">{entry.aiScore}</span>
        </div>
      </td>
      <td className="hidden sm:table-cell px-3 sm:px-5 py-3.5 whitespace-nowrap">
        <span className="text-[11px] font-mono text-zinc-500">{entry.timestamp}</span>
      </td>
    </tr>
  );
}

export default function SignalHistorySection() {
  const [signals, setSignals] = useState<HistoryRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const { t } = useLanguage();

  const loadHistory = async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const res = await fetch('/api/signals/history?limit=40');
      const json = await res.json();
      if (res.status === 401) {
        // Not logged in — show empty history, not an error
        setSignals([]);
        return;
      }
      if (!res.ok) throw new Error(json.error || 'Failed to load history');
      const rows: HistoryRow[] = (json.signals || []).slice(0, 40).map(mapDbToRow);
      setSignals(rows);
    } catch (err: any) {
      setFetchError(err.message || 'Failed to load signal history');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const filtered = signals.filter((s) => {
    const dirMatch = directionFilter === 'ALL' || s.direction === directionFilter;
    return dirMatch;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const paginated = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <section className="space-y-4">
      {/* Refresh button */}
      <div className="flex justify-end">
        <button
          onClick={loadHistory}
          disabled={isLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-900 border border-white/5 hover:bg-zinc-800 hover:border-white/10 text-zinc-400 hover:text-zinc-200 text-xs font-medium transition-all duration-150 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
          {t('refresh')}
        </button>
      </div>

      {/* Contact / Community Buttons */}
      <div className="flex flex-wrap gap-2">
        <a
          href="https://t.me/+2T_xXv9Lkms4MmVh"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#229ED9]/10 border border-[#229ED9]/25 text-[#229ED9] hover:bg-[#229ED9]/20 hover:border-[#229ED9]/40 text-xs font-medium transition-all duration-150 active:scale-95"
        >
          <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L7.17 13.667l-2.95-.924c-.64-.203-.658-.64.135-.954l11.57-4.461c.537-.194 1.006.131.97.893z"/>
          </svg>
          Join Telegram Group
        </a>
        <a
          href="mailto:xauenginesupport@gmail.com"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-400 hover:bg-amber-500/20 hover:border-amber-500/40 text-xs font-medium transition-all duration-150 active:scale-95"
        >
          <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2"/>
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
          </svg>
          xauenginesupport@gmail.com
        </a>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-2 sm:gap-3">
        <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap">
          <Filter className="w-3 h-3 text-zinc-600 flex-shrink-0" />
          <span className="text-[11px] font-mono text-zinc-600 flex-shrink-0">{t('direction')}</span>
          {(['ALL', 'BUY', 'SELL'] as DirectionFilter[]).map((d) => (
            <button
              key={`dir-${d}`}
              onClick={() => {
                setDirectionFilter(d);
                setCurrentPage(1);
              }}
              className={`px-2 sm:px-2.5 py-1 rounded-md text-[11px] font-mono font-medium transition-all duration-150 active:scale-95 ${
                directionFilter === d
                  ? d === 'BUY' ? 'bg-emerald-500/15 border border-emerald-500/25 text-emerald-400'
                    : d === 'SELL'? 'bg-rose-500/15 border border-rose-500/25 text-rose-400' : 'bg-amber-500/15 border border-amber-500/25 text-amber-400' :'bg-zinc-800/50 border border-white/5 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
              }`}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="rounded-xl border border-white/5 bg-zinc-900/40 p-10 text-center animate-pulse">
          <div className="h-4 w-32 bg-zinc-800 rounded mx-auto mb-2" />
          <div className="h-3 w-48 bg-zinc-800 rounded mx-auto" />
        </div>
      )}

      {/* Error state */}
      {fetchError && !isLoading && (
        <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-6 text-center">
          <p className="text-sm text-rose-400 font-mono">{fetchError}</p>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !fetchError && filtered.length === 0 && (
        <div className="rounded-xl border border-white/5 bg-zinc-900/40 p-10 text-center">
          <History className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <p className="text-sm font-medium text-zinc-500">
            {signals.length === 0 ? t('noSignalsYet') : t('noSignalsMatch')}
          </p>
          <p className="text-xs text-zinc-600 mt-1">
            {signals.length === 0 ? t('noSignalsHint') : t('adjustFilters')}
          </p>
        </div>
      )}

      {/* Table */}
      {!isLoading && !fetchError && filtered.length > 0 && (
        <div className="rounded-xl border border-white/5 bg-zinc-900/60 overflow-hidden">
          <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
            <table className="w-full min-w-[520px]">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="px-2 sm:px-5 py-3 text-left text-[10px] font-mono text-zinc-500 uppercase tracking-widest whitespace-nowrap">
                    {t('signalId')}
                  </th>
                  <th className="px-2 sm:px-5 py-3 text-left text-[10px] font-mono text-zinc-500 uppercase tracking-widest whitespace-nowrap">
                    {t('directionCol')}
                  </th>
                  <th className="px-2 sm:px-5 py-3 text-left text-[10px] font-mono text-zinc-500 uppercase tracking-widest whitespace-nowrap">
                    {t('entryZone')}
                  </th>
                  <th className="px-2 sm:px-5 py-3 text-left text-[10px] font-mono text-zinc-500 uppercase tracking-widest whitespace-nowrap">
                    {t('aiScore')}
                  </th>
                  <th className="hidden sm:table-cell px-2 sm:px-5 py-3 text-left text-[10px] font-mono text-zinc-500 uppercase tracking-widest whitespace-nowrap">
                    {t('timestamp')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((entry, i) => (
                  <HistoryTableRow key={entry.id + i} entry={entry} isOdd={i % 2 === 1} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-3 sm:px-5 py-3 sm:py-3.5 border-t border-white/5 bg-zinc-900/40">
            <p className="text-[10px] sm:text-[11px] font-mono text-zinc-600">
              {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} / {filtered.length}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-7 h-7 flex items-center justify-center rounded-md bg-zinc-800 border border-white/5 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-95"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
                <button
                  key={`page-${p}`}
                  onClick={() => setCurrentPage(p)}
                  className={`w-7 h-7 flex items-center justify-center rounded-md text-[11px] font-mono transition-all duration-150 active:scale-95 ${
                    currentPage === p
                      ? 'bg-amber-500/20 border border-amber-500/30 text-amber-400' :'bg-zinc-800 border border-white/5 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-7 h-7 flex items-center justify-center rounded-md bg-zinc-800 border border-white/5 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-95"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}